import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

// Animated keyword component for staggered color reveal
const AnimatedKeyword = ({
  children,
  colorClass,
  hoverClass,
  delay,
  onClick
}: {
  children: React.ReactNode;
  colorClass: string;
  hoverClass: string;
  delay: number;
  onClick: () => void;
}) => {
  return (
    <motion.span
      className={`${colorClass} cursor-pointer ${hoverClass} transition-colors inline-block`}
      initial={{ filter: "grayscale(100%)", opacity: 0.5 }}
      animate={{ filter: "grayscale(0%)", opacity: 1 }}
      transition={{ duration: 0.35, delay, ease: "easeOut" }}
      onClick={onClick}
    >
      {children}
    </motion.span>
  );
};

// Simple fade-in wrapper for non-interactive words
const FadeWord = ({
  children,
  delay,
  className = ""
}: {
  children: React.ReactNode;
  delay: number;
  className?: string;
}) => (
  <motion.span
    className={`inline-block ${className}`}
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.35, delay, ease: "easeOut" }}
  >
    {children}
  </motion.span>
);

interface HeroHeaderProps {
  scrollToSection: (sectionId: string) => void;
}

export const HeroHeader = ({ scrollToSection }: HeroHeaderProps) => {
  return (
    <div className="flex-1 flex items-start pt-20 pb-20 sm:pt-0 sm:pb-0 sm:items-center justify-center">
      <div className="text-center">
        <h1 className="font-extrabold text-[2.5rem] sm:text-5xl lg:text-7xl tracking-tight leading-[1.1] mb-12 sm:mb-8">
          <FadeWord delay={0}>AI</FadeWord>{" "}
          <FadeWord delay={0.15}>platform that</FadeWord>{" "}
          <AnimatedKeyword
            colorClass="text-primary"
            hoverClass="hover:text-primary/80"
            delay={0.3}
            onClick={() => scrollToSection('manage-section')}
          >
            manages
          </AnimatedKeyword>
          <br />
          <FadeWord delay={0.45}>and</FadeWord>{" "}
          <AnimatedKeyword
            colorClass="text-grow"
            hoverClass="hover:text-grow/80"
            delay={0.55}
            onClick={() => scrollToSection('grow-section')}
          >
            grows
          </AnimatedKeyword>
          {" "}
          <FadeWord delay={0.65}>your</FadeWord>{" "}
          <AnimatedKeyword
            colorClass="text-secondary"
            hoverClass="hover:text-secondary/80"
            delay={0.75}
            onClick={() => scrollToSection('business-section')}
          >
            business
          </AnimatedKeyword>
        </h1>
        <motion.p
          className="hidden sm:block mt-4 sm:mt-6 text-lg sm:text-xl lg:text-2xl max-w-none sm:max-w-3xl mx-auto px-4 sm:px-0 text-muted-foreground leading-relaxed"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.35, delay: 1.1, ease: "easeOut" }}
        >
          One person. One platform. Full business.
        </motion.p>
        <div className="h-0" />
        <motion.p
          className="text-lg sm:text-xl lg:text-2xl max-w-none sm:max-w-4xl mx-auto px-4 sm:px-0 text-foreground sm:text-muted-foreground leading-relaxed"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.35, delay: 1.25, ease: "easeOut" }}
        >
          From employees to customers, financials to branding.
        </motion.p>
        <motion.p
          className="text-lg sm:text-xl lg:text-2xl max-w-none sm:max-w-4xl mx-auto px-4 sm:px-0 text-foreground sm:text-muted-foreground leading-relaxed"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.35, delay: 1.4, ease: "easeOut" }}
        >
          All from one Inn - TagInn!
        </motion.p>
        <motion.div
          className="mt-8 sm:mt-12 flex items-center justify-center gap-x-6 flex-wrap gap-y-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.35, delay: 1.5, ease: "easeOut" }}
        >
          <Button size="lg" className="px-6 sm:px-8 bg-primary hover:bg-primary/90 text-base sm:text-lg" asChild data-showable="signup-button">
            <Link to="/auth?mode=sign-up">
              Get Started Free <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </motion.div>
      </div>
    </div>
  );
};
